/**
 * Role-Specific Navigation Configurations
 *
 * Defines tailored nav structures per workspace role. These override
 * the default nav when a workspace is active, creating a focused
 * experience while keeping the shared data model underneath.
 */

import {
  BookOpen,
  Brain,
  Building2,
  CalendarDays,
  ClipboardList,
  CreditCard,
  FileText,
  Globe,
  GraduationCap,
  Handshake,
  Headset,
  Layers,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Receipt,
  Rocket,
  ShieldAlert,
  Target,
  Users,
  Users2,
  Wrench,
} from "lucide-react";
import type React from "react";
import type { WorkspaceRole } from "@/config/roles";

interface RoleNavChild {
  to: string;
  label: string;
  icon: React.ElementType;
  module?: string;
}

export interface RoleNavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  children?: RoleNavChild[];
  module?: string;
  adminOnly?: boolean;
}

/**
 * Executive Director — Strategic command center
 * Focus: dashboards, reports, revenue, high-level summaries
 */
export const getExecutiveNav = (): RoleNavItem[] => [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/intelligence", icon: Brain, label: "Intelligence", module: "executive_intelligence" },
  { to: "/reports", icon: FileText, label: "Reports", module: "executive_intelligence" },
  {
    to: "/billing",
    icon: CreditCard,
    label: "Revenue",
    children: [
      { to: "/member-success?tab=plans", icon: Layers, label: "Plans & Tiers", module: "membership" },
      { to: "/billing", icon: Receipt, label: "Billing" },
      { to: "/sponsorship", icon: Handshake, label: "Sponsorship", module: "sponsors" },
    ],
  },
  {
    to: "/member-success",
    icon: Users,
    label: "Member Success",
    module: "membership",
    children: [
      { to: "/member-success", icon: Users, label: "Overview", module: "membership" },
      { to: "/members", icon: Users, label: "Directory", module: "membership" },
      { to: "/member-success?tab=renewals", icon: ShieldAlert, label: "Renewals", module: "membership" },
    ],
  },
  { to: "/events", icon: CalendarDays, label: "Events", module: "events" },
  { to: "/support", icon: Headset, label: "Support", module: "support_ops" },
  { to: "/settings", icon: Wrench, label: "Settings", adminOnly: true },
];

/**
 * Membership Pro — Growth & pipeline center
 * Focus: members, renewals, prospects, engagement
 */
export const getMembershipProNav = (teamLabel: string): RoleNavItem[] => [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  {
    to: "/member-success",
    icon: Users,
    label: "Member Success",
    module: "membership",
    children: [
      { to: "/member-success", icon: Users, label: "Overview", module: "membership" },
      { to: "/crm", icon: Headset, label: "Prospects" },
      { to: "/companies", icon: Building2, label: "Companies", module: "membership" },
      { to: "/members", icon: Users, label: "Directory", module: "membership" },
    ],
  },
  { to: "/member-success?tab=plans", icon: Layers, label: "Plans & Tiers", module: "membership" },
  { to: "/member-success?tab=renewals", icon: ShieldAlert, label: "Renewals", module: "membership" },
  { to: "/events", icon: CalendarDays, label: "Events", module: "events" },
  { to: "/programs", icon: GraduationCap, label: "Programs", module: "programs" },
  { to: "/community", icon: MessageSquare, label: "Community", module: "community" },
  { to: "/communications", icon: Mail, label: "Communications", module: "communications_pro" },
  { to: "/committees", icon: Users2, label: teamLabel },
  { to: "/settings", icon: Wrench, label: "Settings", adminOnly: true },
];

/**
 * Event Planner — Operations center
 * Focus: events, registrations, check-in, programs
 */
export const getEventPlannerNav = (teamLabel: string): RoleNavItem[] => [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  {
    to: "/events",
    icon: CalendarDays,
    label: "Events",
    module: "events",
    children: [{ to: "/events", icon: CalendarDays, label: "All Events", module: "events" }],
  },
  {
    to: "/programs",
    icon: GraduationCap,
    label: "Programs",
    module: "programs",
    children: [
      { to: "/programs", icon: GraduationCap, label: "Cohorts", module: "programs" },
      { to: "/learning", icon: BookOpen, label: "Learning", module: "programs" },
    ],
  },
  { to: "/communications", icon: Mail, label: "Communications", module: "communications_pro" },
  { to: "/sponsorship", icon: Handshake, label: "Sponsors", module: "sponsors" },
  { to: "/committees", icon: Users2, label: teamLabel },
  { to: "/settings", icon: Wrench, label: "Settings", adminOnly: true },
];

/**
 * Marketing & Comms — Audience & campaign center
 * Focus: website, campaigns, email, content, segments
 */
export const getMarketingNav = (): RoleNavItem[] => [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  {
    to: "/website",
    icon: Globe,
    label: "Sites",
    children: [
      { to: "/website", icon: Globe, label: "Website", module: "website_pro" },
      { to: "/funnels", icon: Rocket, label: "Campaign Funnels", module: "campaign_funnels" },
      { to: "/response-studio", icon: ClipboardList, label: "Response Studio", module: "response_studio" },
    ],
  },
  { to: "/communications", icon: Mail, label: "Campaigns", module: "communications_pro" },
  { to: "/community", icon: MessageSquare, label: "Community", module: "community" },
  {
    to: "/member-success",
    icon: Target,
    label: "Leads",
    children: [
      { to: "/crm", icon: Headset, label: "Pipeline" },
      { to: "/members", icon: Users, label: "Contacts", module: "membership" },
    ],
  },
  { to: "/events", icon: CalendarDays, label: "Events", module: "events" },
  { to: "/settings", icon: Wrench, label: "Settings", adminOnly: true },
];

/**
 * Returns role-specific nav items, or null to fall back to default nav.
 */
export const getRoleNav = (role: WorkspaceRole | null, teamLabel: string): RoleNavItem[] | null => {
  switch (role) {
    case "executive_director":
      return getExecutiveNav();
    case "membership_pro":
      return getMembershipProNav(teamLabel);
    case "event_planner":
      return getEventPlannerNav(teamLabel);
    case "marketing_comms":
      return getMarketingNav();
    default:
      return null; // Fall back to default full nav
  }
};
